import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { AppShell } from '../design-system/shell/app-shell';
import { NavIdentity } from '../design-system/shell/nav';
import { PageHeader } from '../design-system/shell/page-header';
import { GoalProgressCard } from '../design-system/shell/goal-progress-card';
import { SignInPanel } from '../design-system/shell/sign-in-panel';
import { ActionButton } from '../design-system/concept/action-button';
import { CoachCard } from '../design-system/concept/coach-card';
import { ScanLog, type ScanLogEntry } from '../design-system/scan/scan-log';
import { ChangeSinceStart } from '../design-system/progress/change-since-start';
import { FatLeanChart } from '../design-system/composition/fat-lean-chart';
import { MeasurementGrid } from '../design-system/measurement/measurement-grid';
import { NotesCard, type ScanOption } from '../design-system/coach/notes-card';
import { CoachMessagesCard } from '../design-system/client/coach-messages-card';
import { NextStepCard } from '../design-system/coach/next-step-card';
import { ScanComparisonSheet } from '../design-system/comparison/scan-comparison-sheet';
import { ShareDialog } from '../design-system/client/share-dialog';
import { SendToClientCard } from '../design-system/client/send-to-client-card';
import { useToast } from '../design-system/primitives/use-toast';

import { ThenNow, ThenNowGoal } from '../components/coach/then-now-goal';
import { dashboardNav, landingSection, type SectionKey } from '../components/dashboard/nav-entries';
import { GoalSection } from '../components/dashboard/goal-section';
import { AvatarSection } from '../components/dashboard/avatar-section';
import { SettingsSection } from '../components/dashboard/settings-section';
import { CompositionPanel } from '../components/dashboard/composition-panel';
import { FeedbackDialog } from '../components/dashboard/FeedbackDialog';

import { useAuth } from '../context/AuthContext';
import { AuthProvider, initSession, sendLinkEmail } from '../lib/http-service';
import { sessionPath } from '../lib/scan/sessionPath';
import { ClientDataHolder } from '../lib/dashboard/data';
import { useNotes } from '../lib/dashboard/useNotes';
import { buildCompositionSeries } from '../lib/dashboard/composition';
import { buildProgressSummary } from '../lib/dashboard/progress';
import { bodyFatReadings, columnReading, goalProgressFromBodyFat } from '../lib/dashboard/bodyFat';
import { buildMeasurementSeries } from '../lib/dashboard/measurementSeries';
import { buildMeasurementDeltas, metricPair, metricReadings } from '../lib/dashboard/measurementDelta';
import { roundMetric } from '../design-system/lib/metric-format';
import { describeLoadError, type LoadErrorCopy } from '../lib/load-error';
import { buildScanComparison } from '../lib/dashboard/scanComparison';
import { useGoalProjection } from '../lib/dashboard/useGoalProjection';
import { getBodyFatTable } from '../lib/dashboard/bodyfatTable';
import { currentConfig } from '../lib/domainConfig';
import { hasFeature, noun } from '../lib/features';
import { useIsDesktop } from '../lib/useMediaQuery';
import type { ClientData, MeasureData, ShareLinkData } from '../lib/interfaces';
import type { ObjectModel } from '../lib/types';

/**
 * The client dashboard — one page, two chromes.
 *
 * Replaces the `IonTabs` page wholesale. That one rendered a phone tab bar at
 * every viewport width, because `ion-tabs` has no desktop form; the palette
 * work could never reach it. Here the section is state, the rail renders it on
 * desktop and the tab bar on phone, and the desktop `overview` is the
 * concept's Section A in full.
 */
const COACH_ONLY: SectionKey[] = ['goal', 'notes'];

// Fixed for the life of the page: a host, or an override read once at load.
// Retail measures for fit, so it has none of these.
const COMPOSITION = hasFeature('composition');
const GOAL = hasFeature('goal');
const WEIGHT = hasFeature('weight');
const BODY_FAT = hasFeature('bodyFat');
const NAV = dashboardNav();
/** What a shared dashboard shows, as the share copy names it. */
const SHARED_SUMMARY = GOAL ? 'Then, Now and Goal' : 'Then and Now';

/**
 * The tape readings a retail hero compares, in this order, where the
 * deployment's measurement list has them: chest, waist (by sex) and hip,
 * the three a size is bought on.
 */
const HERO_TAPE = ['chest', 'waist', 'waist_female', 'buttocks'];

const ClientDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const search = new URLSearchParams(useLocation().search);
  const isDesktop = useIsDesktop();
  const { toast } = useToast();

  const { isAuthenticated, getAccessTokenSilently, isLoading, signIn, user } =
    useAuth();
  const [loadError, setLoadError] = useState<LoadErrorCopy | null>(null);

  const code = search.get('code') || undefined;
  const cookieHint = search.get('cookie') === '1';
  const [auth] = useState(
    () => new AuthProvider(getAccessTokenSilently, code, id, undefined, cookieHint),
  );
  // A share link or cookie means the reader is the client, not the coach.
  const isClientView = auth.anonymous() || cookieHint;

  const [clientData, setClientData] = useState<ClientData>();
  const [measurements, setMeasurements] = useState<MeasureData[]>([]);
  // Whether the scans have arrived at all. Until they have, "no reading" and
  // "not loaded yet" look identical — an empty list — and the Target page drew
  // the second as "Now 0 % · Underfat".
  const [scansLoaded, setScansLoaded] = useState(false);
  const [models, setModels] = useState<ObjectModel[]>([]);
  const [shared, setShared] = useState<ShareLinkData>();
  // A body fat the coach is trying on the Target page, not yet saved. The
  // projected twin there follows it, so the body matches the number above it.
  const [tryingTarget, setTryingTarget] = useState<number | null>(null);
  const [compare, setCompare] = useState<string[]>([]);
  // Loaded once the client has, not before: a signed-out visitor would fire a
  // request that fails, and nothing would retry it after they signed in.
  const notesApi = useNotes(clientData ? id : undefined, auth);

  // Target and Notes are the coach's tools, so a client has neither section.
  // They see the goal on Home and, on desktop, in the rail and the hero; and
  // what their coach wrote for them in "From your coach" on Home. An old link
  // carrying ?show=goal or ?show=notes lands them on Home.
  //
  // A section this deployment does not have — ?show=goal on retail — lands on
  // Home the same way.
  const navEntries = isClientView
    ? NAV.filter((e) => !COACH_ONLY.includes(e.key as SectionKey))
    : NAV;
  const [section, setSection] = useState<SectionKey>(() =>
    landingSection(search.get('show'), navEntries),
  );

  const [holder] = useState(
    () =>
      new ClientDataHolder(
        id,
        auth,
        setClientData,
        (m: MeasureData[]) => {
          setMeasurements(m);
          setScansLoaded(true);
        },
        setModels,
        setShared,
      ),
  );

  const load = () => {
    setLoadError(null);
    // load() swallows its own failures in places, so a rejection here is the
    // last chance to say something rather than wait forever.
    // Said in the coach's terms: the server's own body ("boom", a stack
    // fragment) is nothing they can act on. The status survives as small print.
    holder.load().catch((e) => setLoadError(describeLoadError(e)));
  };

  useEffect(() => {
    if (isAuthenticated) {
      load();
    } else if (!isLoading && (code || cookieHint)) {
      // A code is traded for the cookie on the first request. Only ?cookie=1,
      // which a saved scan sends, means the browser already holds one —
      // marking a code as done too skipped the trade, so a link opened on the
      // client's own phone loaded nothing.
      if (!code) holder.auth.isCookieSet = true;
      load();
    }
  }, [id, isAuthenticated, isLoading]);

  const chronological = useMemo(
    () =>
      [...measurements].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [measurements],
  );

  const series = useMemo(
    () => (clientData ? buildCompositionSeries(measurements, clientData) : null),
    [measurements, clientData],
  );
  const summary = useMemo(
    () => (clientData ? buildProgressSummary(measurements, clientData) : null),
    [measurements, clientData],
  );
  // Percentages come from here, not from `summary`. The summary is built on
  // the composition series, which needs a weight on each scan; body fat does
  // not, and real scans often carry no weight. Reading "Now" from the summary
  // showed 0 % for a client whose scans were unweighed, and a stale figure for
  // one whose latest scan was.
  const bf = useMemo(() => bodyFatReadings(measurements), [measurements]);
  // Lean mass does need a weight, so it comes from the latest weighed scan —
  // or is absent, and the surfaces that need it say so rather than show 0 kg.
  const leanNowKg = series?.latest?.leanKg;
  const measurementSeries = useMemo(
    () => (clientData ? buildMeasurementSeries(measurements, clientData) : []),
    [measurements, clientData],
  );
  // The same table the band label uses, as regions for the target slider.
  const zones = useMemo(
    () =>
      clientData
        ? getBodyFatTable(clientData.gender, clientData.age).map((c) => ({
            label: c.cat,
            min: c.min,
            max: c.max,
          }))
        : [],
    [clientData],
  );

  // The band a reading falls in, as a word. The coloured strip is gone — see
  // measurement-card.tsx — but "24 % is Overfat for a 41-year-old man" is a
  // clinical fact, and it was the label carrying that, not the colour.
  const bodyFatCategory = useMemo(() => {
    const now = bf.now?.percent;
    if (!clientData || now === undefined) return undefined;
    const table = getBodyFatTable(clientData.gender, clientData.age);
    return table.find((c) => now >= c.min && now <= c.max)?.cat;
  }, [clientData, bf]);

  // No target where there are no goals: retail never projects a body, and a
  // stray target on the profile must not draw one.
  const targetBodyFat = GOAL ? clientData?.target?.measurements?.body_fat : undefined;

  const subject = clientData
    ? {
        gender: clientData.gender === 'female' ? ('female' as const) : ('male' as const),
        heightCm: clientData.height,
      }
    : undefined;
  // /generate takes seconds; `loading` is what lets the twin say a new body is
  // on the way rather than sit showing the previous one.
  const {
    goal,
    model: goalModel,
    loading: goalLoading,
  } = useGoalProjection(holder, targetBodyFat, leanNowKg, subject);
  const trying =
    tryingTarget !== null && tryingTarget !== targetBodyFat ? tryingTarget : undefined;
  const tried = useGoalProjection(holder, trying, leanNowKg, subject);

  if (!clientData) {
    // Four states, not one. A share link authenticates by cookie and needs no
    // sign-in; everything else does, and saying "Loading client…" to someone
    // who is not signed in waits for something that will never arrive.
    const shareLink = Boolean(code || cookieHint);
    const state = loadError
      ? 'error'
      : isLoading
        ? 'checking'
        : !isAuthenticated && !shareLink
          ? 'signed-out'
          : 'loading';
    return (
      <SignInPanel
        state={state}
        subject="client"
        error={loadError?.message}
        errorDetail={loadError?.detail}
        onSignIn={() => signIn()}
        onRetry={load}
        // A 404 here means the id is not on this account — retrying will keep
        // failing, so the roster is the only useful move.
        onBack={isClientView ? undefined : () => history.push('/clients')}
      />
    );
  }

  const name = `${clientData.firstName} ${clientData.lastName}`.trim();

  // Female clients carry the narrowest-waist figure in waist_female; males the
  // navel-height one in waist. Reading the wrong field compares two different
  // measurements.
  const waistOf = (m?: MeasureData) =>
    clientData.gender === 'female'
      ? m?.measurements.waist_female || undefined
      : m?.measurements.waist || undefined;

  // One pair for every waist on the page — first scan with a waist and the
  // latest — and one rounding rule (metricPair). The hero took the body-fat
  // scans and rounded before subtracting, the Avatar list and Measurements
  // cards took their own pair and rounded after, so one waist read −5 here
  // and −4 a tab away. A latest waist older than Now carries its date.
  const waistKey = clientData.gender === 'female' ? 'waist_female' : 'waist';
  const waistPair = metricPair(measurements, waistKey);
  const waistLatest = waistPair?.now ?? metricReadings(measurements, waistKey).at(-1);
  const waist = {
    start: waistPair?.start.value,
    now: waistLatest?.value,
    change: waistPair?.change,
    nowAsOf:
      waistLatest && bf.now && waistLatest.date.getTime() < bf.now.date.getTime()
        ? waistLatest.date
        : undefined,
  };

  // Change needs two readings of body fat — not two weighed scans. The hero
  // and the change strip both waited on the kilogram summary, so a client
  // whose scans were unweighed had neither.
  const canShowChange = BODY_FAT && bf.readings.length >= 2 && bf.start && bf.now;
  const change = canShowChange && COMPOSITION && (
    <ChangeSinceStart
      summary={summary}
      bodyFat={{ start: bf.start!.percent, now: bf.now!.percent }}
      waist={waist}
      weighedThrough={
        summary && summary.now.date.getTime() < bf.now!.date.getTime()
          ? summary.now.date
          : undefined
      }
    />
  );

  const latest = chronological[chronological.length - 1];
  const goalFatKg = goal?.fatKg ?? null;

  // Only real numbers reach the log. Body fat is NaN when the tape cannot
  // produce one — a waist at or below the neck puts a negative inside the
  // formula's log — and the log printed that as "NaN %". Missing reads as "—".
  const finite = (v?: number) => (Number.isFinite(v) ? v : undefined);
  const entries: ScanLogEntry[] = [...chronological].reverse().map((m) => ({
    id: m.ID,
    date: new Date(m.createdAt),
    weightKg: WEIGHT ? finite(m.measurements.weight) : undefined,
    bodyFatPercent: BODY_FAT ? finite(m.measurements.body_fat) : undefined,
  }));
  // The comparison sheet is a composition comparison — weight, fat and lean
  // in kilograms — so picking two scans means nothing without composition.
  const onToggleCompare = COMPOSITION ? (scanId: string) => toggleCompare(scanId) : undefined;

  const comparison =
    compare.length === 2
      ? buildScanComparison(
          measurements,
          {
            gender: clientData.gender === 'female' ? 'female' : 'male',
            age: clientData.age,
          },
          compare[0],
          compare[1],
        )
      : null;

  const toggleCompare = (scanId: string) =>
    setCompare((prev) =>
      prev.includes(scanId)
        ? prev.filter((x) => x !== scanId)
        : // Keep the most recent pick and the new one, so a third tap replaces
          // the older of the two rather than doing nothing.
          [...prev, scanId].slice(-2),
    );

  // Each earlier scan's own place on the road to the target, for the timeline.
  const scanFractions =
    bf.start && targetBodyFat
      ? bf.readings
          .slice(1, -1)
          .map((r) => (bf.start!.percent - r.percent) / (bf.start!.percent - targetBodyFat))
      : [];
  const goalProgressNow =
    bf.start && bf.now ? goalProgressFromBodyFat(bf.start.percent, bf.now.percent, targetBodyFat) : null;

  /** A fresh session, as a path this origin can serve. See sessionPath. */
  const newScanPath = async () => sessionPath(clientData.ID, await initSession(clientData, holder.auth));

  const startScan = async () => {
    try {
      history.push(await newScanPath());
    } catch (e) {
      toast({
        title: 'Could not start a scan',
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    }
  };

  // Circumferences only: weight and body fat have their own cards and would
  // otherwise appear twice on the avatar scrubber.
  const visibleMeasurements = (
    clientData.gender === 'female'
      ? currentConfig.measurements.female
      : currentConfig.measurements.male
  ).filter((m) => m.key !== 'weight' && m.key !== 'body_fat');

  // Retail: the first scan against the latest, by the tape a size is chosen
  // on. Two scans or none; one scan has nothing to compare.
  const heroTape = visibleMeasurements
    .filter((m) => HERO_TAPE.includes(m.key))
    .sort((a, b) => HERO_TAPE.indexOf(a.key) - HERO_TAPE.indexOf(b.key));
  const retailHero = !BODY_FAT && chronological.length >= 2 && (
    <ThenNow
      start={{ date: new Date(chronological[0].createdAt), values: chronological[0].measurements }}
      now={{ date: new Date(latest.createdAt), values: latest.measurements }}
      tape={heroTape}
      models={{
        start: models[0] ?? null,
        now: models.length ? models[models.length - 1] : null,
      }}
    />
  );

  const hero = !BODY_FAT ? retailHero : canShowChange && (
    <ThenNowGoal
      start={columnReading(bf.start!, series?.points ?? [])}
      // An unweighed latest scan shows the last weighed scan's weight, dated —
      // as the Measurements card does — rather than "—" beside its "90 kg".
      now={columnReading(bf.now!, series?.points ?? [], { carry: true })}
      progressFraction={goalProgressNow?.fraction ?? null}
      targetBodyFat={targetBodyFat}
      goal={goal}
      waist={waist}
      scanFractions={scanFractions}
      models={{
        start: models[0] ?? null,
        now: models.length ? models[models.length - 1] : null,
        goal: goalModel,
      }}
    />
  );

  // For the "About" choice, and to label a note written about one scan.
  const scanOptions: ScanOption[] = [...chronological].reverse().map((m) => ({
    id: m.ID,
    label: new Date(m.createdAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
  }));

  // The coach's card, or — for a client — only what was written for them. The
  // server filters a share reader to audience 'client'; nothing here hides a
  // coach note, because none arrives.
  const notesCard = (limit?: number) =>
    isClientView ? (
      <CoachMessagesCard notes={notesApi.notes} className="h-full" />
    ) : (
      <NotesCard
        notes={notesApi.notes}
        status={notesApi.status}
        error={notesApi.error}
        onRetry={notesApi.reload}
        clientName={clientData.firstName || name}
        scans={scanOptions}
        sharingOn={Boolean(shared?.active)}
        onTurnOnSharing={() => holder.onShare(true)}
        onCreate={notesApi.create}
        onUpdate={notesApi.update}
        onDelete={notesApi.remove}
        limit={limit}
        onShowAll={limit ? () => setSection('notes') : undefined}
        className="h-full"
      />
    );
  const notes = notesCard(3);

  // Withheld from a share reader: a client looking at their own dashboard has
  // no one to send a link to, and the scan link would let them start sessions
  // on the coach's account.
  const sendToClient = !isClientView && (
    <SendToClientCard
      clientName={clientData.firstName || name}
      onCreateScanLink={async () =>
        `${window.location.origin}${await newScanPath()}`
      }
      dashboardLink={
        shared?.active
          ? `${window.location.origin}/dashboard/${clientData.ID}?code=${shared.code}`
          : undefined
      }
      clientEmail={clientData.email}
      onEmailLink={(kind) => sendLinkEmail(clientData.ID, kind, holder.auth)}
      lastSent={(clientData as { lastSent?: Partial<Record<'scan' | 'dashboard', string>> }).lastSent}
      noun={noun.client}
      dashboardSummary={SHARED_SUMMARY}
      dashboardAction={
        <ShareDialog
          clientID={clientData.ID}
          clientName={clientData.firstName || name}
          shared={shared}
          onShare={(enabled) => holder.onShare(enabled)}
          noun={noun.client}
          summary={SHARED_SUMMARY}
        />
      }
    />
  );

  // Muscle mass is gated inside, on currentConfig.showMuscleMass.
  const composition = (
    <CompositionPanel
      series={series}
      goalFatKg={goalFatKg}
      clientName={clientData.firstName || 'this client'}
    />
  );

  // The tape review, from a scan row. On a phone Settings — its other home —
  // is behind the ⋯ menu, two taps from the scan the coach is looking at.
  const reviewTape = (scanId: string) => {
    const m = measurements.find((x) => x.ID === scanId);
    if (!m) return null;
    const date = new Date(m.createdAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return (
      <FeedbackDialog
        gender={clientData.gender}
        measureData={m}
        onUpdateMeasurement={(u) => holder.onConfirmMeasurement(u)}
        trigger={
          <button
            type="button"
            aria-label={`Review tape for the scan from ${date}`}
            data-testid="review-tape"
            className="flex min-h-11 shrink-0 items-center rounded-lg border border-border bg-card px-3 text-[11px] font-medium text-foreground transition-colors hover:bg-shell"
          >
            Review tape
          </button>
        }
      />
    );
  };

  const renderSection = () => {
    switch (section) {
      case 'avatar':
        return (
          <AvatarSection
            models={models}
            goalModel={GOAL ? goalModel : null}
            goalBodyFat={targetBodyFat}
            visibleMeasurements={visibleMeasurements}
            deltas={buildMeasurementDeltas(
              measurements,
              visibleMeasurements.map((m) => m.key),
            )}
          />
        );

      case 'measurements':
        return (
          <MeasurementGrid
            series={measurementSeries}
            targetBodyFat={targetBodyFat}
            bodyFatCategory={BODY_FAT ? bodyFatCategory : undefined}
          />
        );

      case 'composition':
        // The phone's Statistics tab. It absorbs History and Measurements,
        // which the tab bar has no room to carry separately.
        return (
          <div className="flex flex-col gap-3.5">
            {change}
            <div className="grid gap-3.5 lg:grid-cols-2">{composition}</div>
            {!isDesktop && (
              <MeasurementGrid
                series={measurementSeries}
                targetBodyFat={targetBodyFat}
                bodyFatCategory={bodyFatCategory}
              />
            )}
          </div>
        );

      case 'history':
        return (
          <div className="flex flex-col gap-3.5">
            {series && series.points.length > 1 && (
              <FatLeanChart series={series} goalFatKg={goalFatKg} />
            )}
            <MeasurementGrid
              series={measurementSeries}
              targetBodyFat={targetBodyFat}
              bodyFatCategory={bodyFatCategory}
            />
          </div>
        );

      case 'goal':
        // A target is set against where the client is now. Without a reading
        // there is no "now", and 0 % is not one — it drew the marker at the
        // bottom of the scale and called the client Underfat.
        if (!bf.now) {
          return (
            <CoachCard testId={scansLoaded ? 'goal-no-reading' : 'goal-loading'} title="Body fat target">
              <p className="text-[12.5px] leading-relaxed text-foreground-soft">
                {scansLoaded
                  ? 'No scan has a body fat reading yet, so there is nothing to set a target from. It appears after the first complete scan.'
                  : loadError
                    ? `The scans did not load. ${loadError.message}`
                    : 'Loading scans…'}
              </p>
            </CoachCard>
          );
        }
        return (
          <GoalSection
            // One decimal, as the hero and the Measurements card show it.
            currentPercent={roundMetric('body_fat', bf.now.percent)}
            targetPercent={targetBodyFat}
            leanHeldKg={leanNowKg}
            zones={zones}
            age={clientData.age}
            goal={trying !== undefined ? tried.goal : goal}
            goalModel={trying !== undefined ? tried.model : goalModel}
            nowModel={models.length ? models[models.length - 1] : null}
            regenerating={trying !== undefined ? tried.loading : goalLoading}
            // Trying a value builds its body without saving anything; the
            // client sees a target only once it is set.
            onTry={setTryingTarget}
            // setTarget persists; the committed projection is keyed on
            // clientData.target, so the twin follows once that lands — at once,
            // since the value was just tried and its body is cached. A client
            // never reaches this section (see navEntries), and the API refuses
            // a share-link reader regardless. A refused save is said, and
            // the slider goes back to the saved target.
            onCommit={async (pct) => {
              try {
                await holder.setTarget(pct);
                return true;
              } catch {
                toast({
                  title: "Couldn't save the target",
                  description: 'Nothing was changed. Try again.',
                  variant: 'destructive',
                });
                return false;
              }
            }}
          />
        );

      case 'notes':
        return (
          <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            {notesCard()}
            <NextStepCard lastScan={latest ? new Date(latest.createdAt) : undefined} />
          </div>
        );

      case 'settings':
        // Scan management and client deletion had no home at all after the
        // edit pencil went with ClientAvatar. This is that home.
        return isClientView ? (
          <p className="text-sm text-muted-foreground">
            Settings are only available to the coach who owns this client.
          </p>
        ) : (
          <SettingsSection
            gender={clientData.gender}
            clientName={name}
            client={clientData}
            onSaveDetails={(patch) => holder.updateClient(patch)}
            measurements={measurements}
            onConfirmMeasurement={(m) => holder.onConfirmMeasurement(m)}
            onDeleteMeasurement={(ids) => holder.deleteMeasurements(ids)}
            onDeleteClient={async () => {
              const ok = await holder.deleteClient();
              if (ok) history.push('/clients');
            }}
          />
        );

      case 'overview':
      default:
        if (!COMPOSITION && isDesktop) {
          // Retail's Home: the tape hero, then one row of scans, notes and
          // what comes next. The composition cells the fitness overview has
          // between them are gone, so the two rows close into one rather than
          // leaving a scan log alone in a row built for three.
          return (
            <div className="flex flex-col gap-3.5">
              {hero}
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)] gap-3.5">
                <ScanLog entries={entries} readings={false} className="h-full" />
                {notes}
                <div className="flex flex-col gap-3.5">
                  <NextStepCard lastScan={latest ? new Date(latest.createdAt) : undefined} />
                  {sendToClient}
                </div>
              </div>
            </div>
          );
        }
        return isDesktop ? (
          <div className="flex flex-col gap-3.5">
            {hero}
            {change}
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)] gap-3.5">
              {composition}
              <ScanLog
                entries={entries}
                selected={compare}
                onToggle={onToggleCompare}
                className="h-full"
              />
            </div>
            <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3.5">
              {notes}
              <NextStepCard lastScan={latest ? new Date(latest.createdAt) : undefined} />
              {sendToClient}
            </div>
            {GOAL && (
              <p className="text-[10.5px] text-muted-foreground">
                {leanNowKg === undefined
                  ? 'The goal column is simulated from the target body fat; its weight needs a weighed scan.'
                  : "The goal column is a projection at the client's current lean mass."}{' '}
                It is not a scan and not a forecast of when.
              </p>
            )}
          </div>
        ) : (
          // Phone Home, per section B: identity, the one sentence of change
          // quality, the scan log, notes.
          <div className="flex flex-col gap-3.5">
            {change}
            {GOAL &&
              (targetBodyFat !== undefined || !isClientView) &&
              goalProgress('rounded-lg border border-border bg-card p-5')}
            <ScanLog
              entries={entries}
              selected={compare}
              onToggle={onToggleCompare}
              readings={WEIGHT || BODY_FAT}
              rowAction={isClientView ? undefined : (e) => reviewTape(e.id)}
            />
            {notes}
            <NextStepCard lastScan={latest ? new Date(latest.createdAt) : undefined} />
            {/* The phone had no way to hand a client either link — the header
                actions it would have lived in are desktop-only. */}
            {sendToClient}
          </div>
        );
    }
  };

  const activeLabel = NAV.find((e) => e.key === section)?.label ?? 'Overview';

  // The rail's goal card on desktop, and a card on the phone's Home — which is
  // the only place a client sees their goal on a phone, since the hero and
  // the rail are both desktop-only and they have no Target section.
  const goalProgress = (className?: string) => (
    <GoalProgressCard
      className={className}
      fraction={goalProgressNow ? goalProgressNow.fraction : null}
      targetBodyFat={targetBodyFat}
      pointsDone={goalProgressNow?.pointsDone}
      pointsTotal={goalProgressNow?.pointsTotal}
      onSetTarget={isClientView ? undefined : () => setSection('goal')}
    />
  );

  return (
    <AppShell
      entries={navEntries}
      active={section}
      onSelect={(k) => setSection(k as SectionKey)}
      onHome={isClientView ? undefined : () => history.push('/overview')}
      phoneTitle={name}
      phoneBack={isClientView ? undefined : () => history.push('/clients')}
      // Settings is desktop-only in the tab bar, and a client has none.
      phoneMenu={isClientView ? undefined : navEntries.filter((e) => e.key === 'settings')}
      identity={
        <NavIdentity
          name={user?.name ?? 'Coach'}
          detail={isClientView ? `${noun.Client} view` : 'Coach'}
          imageUrl={user?.picture}
        />
      }
      navFooter={GOAL ? goalProgress() : undefined}
    >
      <div className="mx-auto flex max-w-[1296px] flex-col gap-3.5 p-4 lg:p-7">
        <PageHeader
          eyebrow={section === 'overview' ? `${noun.Client} overview` : activeLabel}
          title={name}
          meta={[
            clientData.age ? `${clientData.age} y` : null,
            clientData.height ? `${clientData.height} cm` : null,
            `${measurements.length} scan${measurements.length === 1 ? '' : 's'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
          /*
           * Home only.
           *
           * These rode the header on every section — goal, notes, avatar,
           * settings — where none of them is the thing you came to do, and on
           * a phone they took a whole row above the fold on all five. "Compare
           * scans" is gone entirely: it scrolled to the scan log and cleared
           * the selection, which is not a comparison and did not say that
           * picking two rows is what makes one. The log now says so itself.
           */
          actions={
            section === 'overview' && !isClientView ? (
              <ActionButton variant="primary" onClick={startScan} testId="new-scan">
                New scan
              </ActionButton>
            ) : undefined
          }
        />
        {renderSection()}
      </div>

      {compare.length === 2 && (
        <ScanComparisonSheet
          comparison={comparison}
          onClose={() => setCompare([])}
          showMuscle={currentConfig.showMuscleMass}
        />
      )}
    </AppShell>
  );
};

export default ClientDashboard;
